import { computed, inject, provide, reactive } from "vue";
import { BASE_API } from "ui/Constants.js";
import { Month } from "shared/enums/Month.js";
import axios from "axios";
import { getLoadableYearAndMonth, isEmpty } from "shared/utilities.js";

let { loadableYear, loadableMonth } = getLoadableYearAndMonth();
loadableYear = {
  label: loadableYear.toString(),
  value: loadableYear,
};
const years = [];
for (let i = loadableYear.value; i >= 2018; i--) {
  years.push({
    value: i,
    label: String(i),
  });
}

const FormStore = {
  init: () => {
    // Form State
    const state = reactive({
      landingScreen: true,
      disableSaveBtn: true,
      showEdit: false,
      isPreview: true,
      isLoading: false,
      hideAcquiredDoors: true,
      hideAcquiredHOADoors: true,
      hideTransferredDoors: true,
      hideTransferredHOADoors: true,
      filterValues: {
        selectedCompany: null,
        selectedYear: loadableYear,
        selectedMonth: Month.options[loadableMonth],
      },
      queriedValues: {
        selectedCompany: null,
        selectedYear: null,
        selectedMonth: null,
      },
      yearOptions: years,
      backupFormData: {},
      previousMonth: {
        totalDoors: null,
        totalHOADoors: null,
      },
      formData: {
        updatedBy: null,
        updatedOn: null,
        doorsTotal: 0,
        doorsAdded: 0,
        totalAcquiredDoors: 0,
        totalAcquiredDoorsUnderHOA: 0,
        doorsRemoved: 0,
        doorsUnderHOA: 0,
        rentCollected: null,
        rentUnpaidZeroToThirty: null,
        rentUnpaidTotal: null,
        occupiedListed: null,
        occupiedUnlisted: null,
        occupiedTotal: null,
        vacantUnlisted: null,
        vacantListed: null,
        vacantTotal: null,
        evictionsInProcess: null,
        comments: null,
        revenueTotal: null,
        totalNewDoorsUnderHOA: 0,
        totalLostDoorsUnderHOA: 0,
        doorsTransferredIn: 0,
        doorsTransferredOut: 0,
        hoaDoorsTransferredIn: 0,
        hoaDoorsTransferredOut: 0,
        revenue: [
          {
            name: "managementFees",
            title: "Management Fees",
            code: 40100,
            billed: null,
            paid: null,
          },
          {
            name: "leasingFees",
            title: "Leasing/Admin Fees",
            code: 40200,
            billed: null,
            paid: null,
          },
          {
            name: "tenantFees",
            title: "Tenant Fees",
            code: 40300,
            billed: null,
            paid: null,
          },
          {
            name: "maintenanceFees",
            title: "Maintenance Fees",
            code: 40400,
            billed: null,
            paid: null,
          },
          {
            name: "applicationFee",
            title: "Application Fee",
            code: 40450,
            billed: null,
            paid: null,
          },
          {
            name: "renewalFees",
            title: "Renewal Fees",
            code: 40451,
            billed: null,
            paid: null,
          },
          {
            name: "brokerageFees",
            title: "Brokerage Fees",
            code: 40500,
            billed: null,
            paid: null,
          },
          {
            name: "commissions",
            title: "Commissions",
            code: 40525,
            billed: null,
            paid: null,
          },
          {
            name: "petInspectionFee",
            title: "Pet Inspection Fee",
            code: 40550,
            billed: null,
            paid: null,
          },
          {
            name: "miscIncome",
            title: "Misc. Income",
            code: 40600,
            billed: null,
            paid: null,
          },
          {
            name: "nsfLateFee",
            title: "NSF/Late Fee",
            code: 40650,
            billed: null,
            paid: null,
          },
        ],
      },
    });

    // Getters
    const getSelectedCompany = computed(
      () => state.filterValues.selectedCompany,
    );
    const previousMonth = computed(() => state.previousMonth);
    const getSelectedYear = computed(() => state.filterValues.selectedYear);
    const getSelectedMonth = computed(() => state.filterValues.selectedMonth);
    const getEditableTimeframe = computed(() => {
      const editableStartYear = state.filterValues.selectedYear?.value;
      const editableStartMonth = state.filterValues.selectedMonth?.value + 1;
      let editableEndYear = state.filterValues.selectedYear?.value;
      let editableEndMonth = state.filterValues.selectedMonth?.value + 1;
      if (editableStartMonth === 12 ) {
        editableEndYear = state.filterValues.selectedYear?.value + 1;
        editableEndMonth = 0;
      }
      const editableStartDayValue = (new Date(`${editableStartYear}-${editableStartMonth}-01`).getEndOfMonth()).getDate() - 3;
      const editableStartDay = new Date(`${editableStartYear}-${editableStartMonth}-${editableStartDayValue}`);
      const editableEndDay = new Date(`${editableEndYear}-${editableEndMonth + 1}-06`);
      editableEndDay.setSeconds(-1);
      return {
        start: editableStartDay,
        end: editableEndDay,
      };
    });
    const getFormData = computed(() => state.formData);
    const getDoorsTotal = computed(() => state.formData.doorsTotal);
    const getDisableSaveBtn = computed(() => state.disableSaveBtn);
    const getShowEdit = computed(
      () =>
        JSON.stringify(state.filterValues) === JSON.stringify(state.queriedValues) && state.filterValues?.selectedCompany !== null && state.filterValues?.selectedMonth?.value !== null && state.filterValues?.selectedYear !== null,
    );
    const getIsPreview = computed(() => state.isPreview);
    const getIsLoading = computed(() => state.isLoading);
    const getLandingScreen = computed(() => state.landingScreen);
    const getMonthOptions = computed(() => {
      const yearValue = parseInt(state.filterValues.selectedYear?.value);
      if (yearValue < loadableYear.value) {
        return Month.options;
      }
      if (yearValue === loadableYear.value) {
        return Month.options.filter((option) => {
          return option.value <= loadableMonth;
        });
      }
      return [];
    });
    const getYearOptions = computed(() => {
      return state.yearOptions;
    });
    const getUpdatedBy = computed(() => {
      return state.formData.updatedBy;
    });
    const getUpdatedOn = computed(() => {
      return state.formData.updatedOn;
    });
    const getHideAcquiredDoors = computed(() => {
      return state.hideAcquiredDoors;
    });
    const getHideTransferredDoors = computed(() => {
      return state.hideTransferredDoors;
    });
    const getHideAcquiredHOADoors = computed(() => {
      return state.hideAcquiredHOADoors;
    });
    const getHideTransferredHOADoors = computed(() => {
      return state.hideTransferredHOADoors;
    });


    // Mutations
    const updateSelectedCompany = (company) => {
      state.filterValues.selectedCompany = company;
    };
    const updateSelectedYear = (year) => {
      state.filterValues.selectedYear = year;
    };
    const setDisableSaveBtn = (value) => {
      state.disableSaveBtn = value;
    };
    const updateSelectedMonth = (month) => {
      state.filterValues.selectedMonth = month;
    };
    const updateDoorsTotal = (doorsTotal) => {
      state.formData.doorsTotal = doorsTotal;
    };
    const updateFormDataKeyValue = (k, v) => {
      state.formData[k] = v;
    };
    const cancel = () => {
      state.isPreview = true;
      hideTransferredAndAcquiredDoors();
      setFormData(Object.assign({}, state.backupFormData));
    };
    const edit = () => {
      state.isPreview = false;
    };
    const setIsLoading = (value) => {
      state.isLoading = value;
    };
    const setIsPreview = (value) => {
      state.isPreview = value;
    };
    const setFormData = (data) => {
      Object.keys(state.formData).forEach((key) => {
        if (key === "revenue") {
          state.formData.revenue.forEach((e) => {
            if (data.revenue) {
              const found = data.revenue.find((i) => e.code === i.code);
              if (found) {
                e.paid = found.paid?.toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }).replaceAll(",", "");
                e.billed = found.billed?.toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }).replaceAll(",", "");
              } else {
                e.paid = null;
                e.billed = null;
              }
            } else {
              e.paid = null;
              e.billed = null;
            }
          });
        } else {
          if (key === "rentCollected" || key === "rentUnpaidZeroToThirty" || key === "rentUnpaidTotal" || key === "revenueTotal") {
            state.formData[key] = data[key]?.toLocaleString("en-US", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            }).replaceAll(",", "");
          } else {
            state.formData[key] = data[key];
          }

          if (isNaN(data[key]) && !data[key]) {
            state.formData[key] = null;
          }
        }
      });
    };
    const setBackupFormData = (value) => {
      if (value) {
        state.backupFormData = value;
      } else {
        const backupRevenue = state.formData.revenue.map((revenue) => {
          return Object.assign({}, revenue);
        });
        state.backupFormData = Object.assign({}, state.formData);
        state.backupFormData.revenue = backupRevenue;
      }
    };
    const setQueriedValues = (value) => {
      state.queriedValues = Object.assign({}, value);
    };
    const setLandingScreen = (value) => {
      state.landingScreen = value;
    };
    const hideTransferredAndAcquiredDoors = () => {
      state.hideAcquiredDoors = true;
      state.hideTransferredDoors = true;
      state.hideAcquiredHOADoors = true;
      state.hideTransferredHOADoors = true;
    };
    const showAcquiredDoors = () => {
      state.hideAcquiredDoors = false;
    };
    const showTransferredDoors = () => {
      state.hideTransferredDoors = false;
    };
    const showAcquiredHOADoors = () => {
      state.hideAcquiredHOADoors = false;
    };
    const showTransferredHOADoors = () => {
      state.hideTransferredHOADoors = false;
    };

    // Actions
    const getMonthlySnapshot = async () => {
      const filters = state.filterValues;
      if (
        !getShowEdit.value && !isEmpty(filters.selectedYear) && !isEmpty(filters.selectedMonth) && !isEmpty(filters.selectedCompany)
      ) {
        setIsLoading(true);
        try {
          const previousMonth = state.filterValues.selectedMonth.value + 1 > 1 ? state.filterValues.selectedMonth.value : 12;
          const previousYear = state.filterValues.selectedMonth.value + 1 > 1 ? state.filterValues.selectedYear.value : state.filterValues.selectedYear.value - 1;
          const previousMonthData = await axios.get(`${BASE_API}/companies/${filters.selectedCompany.value}/year/${previousYear}/month/${previousMonth}`);
          state.previousMonth.totalDoors = previousMonthData.data.doorsTotal || 0;
          state.previousMonth.totalHOADoors = previousMonthData.data.doorsUnderHOA || 0;
        } catch (err) {
          state.previousMonth.totalDoors = 0;
          state.previousMonth.totalHOADoors = 0;
        }

        await axios
          .get(`${BASE_API}/companies/${filters.selectedCompany.value}/year/${filters.selectedYear.value}/month/${filters.selectedMonth.value + 1}`)
          .then((response) => {
            if (response.status === 200) {
              setFormData(response.data);
              setQueriedValues(state.filterValues);
            }
          })
          .catch((err) => {
            if (err.response.status === 404) {
              const emptyFormData = {};
              Object.keys(state.formData).forEach((key) => {
                if (key !== "revenue") emptyFormData[key] = null;
              });
              emptyFormData.revenue = state.formData.revenue.map((revenue) => {
                const revenueObject = Object.assign({}, revenue);
                revenueObject.billed = null;
                revenueObject.paid = null;
                return revenueObject;
              });
              setFormData(emptyFormData);
              setQueriedValues(state.filterValues);
            }
          });
        setLandingScreen(false);
        setBackupFormData();
        setIsLoading(false);
        setDisableSaveBtn(false);
        hideTransferredAndAcquiredDoors();
      }
    };

    const formatFormData = () => {
      setFormData(state.formData);
    };

    const parseFloatFunction = (arg) => {
      let returnValue;
      returnValue = Number.parseFloat(arg);
      if (isNaN(returnValue)) returnValue = null;
      return returnValue;
    };

    const submitForm = (username) => {
      let payload = state.formData;
      payload.updatedBy = username;
      payload.updatedOn = Date.now();
      payload = Object.assign({}, state.formData);
      payload.revenue = [];
      payload.doorsTotal = state.previousMonth.totalDoors + state.formData.doorsAdded + state.formData.totalAcquiredDoors + state.formData.doorsTransferredIn - state.formData.doorsRemoved - state.formData.doorsTransferredOut;
      payload.doorsUnderHOA = state.previousMonth.totalHOADoors + state.formData.totalNewDoorsUnderHOA + state.formData.totalAcquiredDoorsUnderHOA + state.formData.hoaDoorsTransferredIn - state.formData.totalLostDoorsUnderHOA - state.formData.hoaDoorsTransferredOut;
      for (const rev of state.formData.revenue) {
        const newRevenueObject = Object.assign({}, rev);
        newRevenueObject.billed = parseFloatFunction(newRevenueObject.billed);
        newRevenueObject.paid = parseFloatFunction(newRevenueObject.paid);
        payload.revenue.push(newRevenueObject);
      }
      payload.revenueTotal = parseFloatFunction(payload.revenueTotal);
      payload.rentCollected = parseFloatFunction(payload.rentCollected);
      payload.rentUnpaidZeroToThirty = parseFloatFunction(payload.rentUnpaidZeroToThirty);
      payload.rentUnpaidTotal = parseFloatFunction(payload.rentUnpaidTotal);
      const response = axios.put(`${BASE_API}/companies/${state.filterValues.selectedCompany.value}/year/${state.filterValues.selectedYear.value}/month/${state.filterValues.selectedMonth.value + 1}`,
        payload,
      );
      state.formData.doorsTotal = payload.doorsTotal;
      state.formData.doorsUnderHOA = payload.doorsUnderHOA;
      hideTransferredAndAcquiredDoors();
      return response;
    };

    provide("getSelectedCompany", getSelectedCompany);
    provide("getSelectedYear", getSelectedYear);
    provide("getSelectedMonth", getSelectedMonth);
    provide("updateSelectedCompany", updateSelectedCompany);
    provide("updateSelectedYear", updateSelectedYear);
    provide("updateSelectedMonth", updateSelectedMonth);
    provide("getFormData", getFormData);
    provide("getDoorsTotal", getDoorsTotal);
    provide("updateDoorsTotal", updateDoorsTotal);
    provide("updateFormDataKeyValue", updateFormDataKeyValue);
    provide("submitForm", submitForm);
    provide("getMonthlySnapshot", getMonthlySnapshot);
    provide("getDisableSaveBtn", getDisableSaveBtn);
    provide("getShowEdit", getShowEdit);
    provide("cancel", cancel);
    provide("edit", edit);
    provide("getIsPreview", getIsPreview);
    provide("getIsLoading", getIsLoading);
    provide("getMonthOptions", getMonthOptions);
    provide("getYearOptions", getYearOptions);
    provide("setIsPreview", setIsPreview);
    provide("setBackupFormData", setBackupFormData);
    provide("getUpdatedBy", getUpdatedBy);
    provide("getUpdatedOn", getUpdatedOn);
    provide("getLandingScreen", getLandingScreen);
    provide("formatFormData", formatFormData);
    provide("previousMonth", previousMonth);
    provide("getHideAcquiredDoors", getHideAcquiredDoors);
    provide("getHideTransferredDoors", getHideTransferredDoors);
    provide("getHideAcquiredHOADoors", getHideAcquiredHOADoors);
    provide("getHideTransferredHOADoors", getHideTransferredHOADoors);
    provide("showAcquiredDoors", showAcquiredDoors);
    provide("showTransferredDoors", showTransferredDoors);
    provide("showAcquiredHOADoors", showAcquiredHOADoors);
    provide("showTransferredHOADoors", showTransferredHOADoors);
    provide("getEditableTimeframe", getEditableTimeframe);
  },

  inject: () => ({
    getSelectedCompany: inject("getSelectedCompany"),
    getSelectedYear: inject("getSelectedYear"),
    getSelectedMonth: inject("getSelectedMonth"),
    updateSelectedCompany: inject("updateSelectedCompany"),
    updateSelectedYear: inject("updateSelectedYear"),
    updateSelectedMonth: inject("updateSelectedMonth"),
    getFormData: inject("getFormData"),
    getDoorsTotal: inject("getDoorsTotal"),
    updateDoorsTotal: inject("updateDoorsTotal"),
    updateFormDataKeyValue: inject("updateFormDataKeyValue"),
    submitForm: inject("submitForm"),
    getMonthlySnapshot: inject("getMonthlySnapshot"),
    getDisableSaveBtn: inject("getDisableSaveBtn"),
    getShowEdit: inject("getShowEdit"),
    cancel: inject("cancel"),
    edit: inject("edit"),
    getIsPreview: inject("getIsPreview"),
    getIsLoading: inject("getIsLoading"),
    getMonthOptions: inject("getMonthOptions"),
    getYearOptions: inject("getYearOptions"),
    setIsPreview: inject("setIsPreview"),
    setBackupFormData: inject("setBackupFormData"),
    getUpdatedBy: inject("getUpdatedBy"),
    getUpdatedOn: inject("getUpdatedOn"),
    getLandingScreen: inject("getLandingScreen"),
    formatFormData: inject("formatFormData"),
    previousMonth: inject("previousMonth"),
    getHideAcquiredDoors: inject("getHideAcquiredDoors"),
    getHideTransferredDoors: inject("getHideTransferredDoors"),
    getHideAcquiredHOADoors: inject("getHideAcquiredHOADoors"),
    getHideTransferredHOADoors: inject("getHideTransferredHOADoors"),
    showAcquiredDoors: inject("showAcquiredDoors"),
    showTransferredDoors: inject("showTransferredDoors"),
    showAcquiredHOADoors: inject("showAcquiredHOADoors"),
    showTransferredHOADoors: inject("showTransferredHOADoors"),
    getEditableTimeframe: inject("getEditableTimeframe"),
  }),
};

export default FormStore;
